import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Save, Blocks, Code } from 'lucide-react';
import EmailBlockEditor from './email-builder/EmailBlockEditor';
import RichTextEditor from './RichTextEditor';
import { serializeEmailHtml } from './email-builder/emailHtmlSerializer';
import { DEFAULT_EMAIL_SETTINGS, createBlock } from './email-builder/emailBlockRegistry';
import { signatureAssetMergeMap } from '../lib/signatureAssets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * EmailEditorModal — Full-screen email editor with block builder + AI agents.
 * Supports both block-based (new) and legacy HTML (backward compat) modes.
 *
 * Props:
 *   open, onClose, subject, body, onSave({subject, body, email_blocks, email_settings}),
 *   emailBlocks (initial blocks), emailSettings (initial settings),
 *   contactId, dealId, accountId (for AI context)
 */
export default function EmailEditorModal({
    open, onClose,
    subject: initSubject = '', body: initBody = '',
    emailBlocks: initBlocks = null, emailSettings: initSettings = null,
    onSave, contactId, dealId, accountId
}) {
    const { getAuthHeader, user } = useAuth();
    const [subject, setSubject] = useState(initSubject);
    const [body, setBody] = useState(initBody);
    const [emailBlocks, setEmailBlocks] = useState(initBlocks);
    const [emailSettings, setEmailSettings] = useState(initSettings);
    const [signatures, setSignatures] = useState([]);
    const [mode, setMode] = useState('blocks');
    const [saving, setSaving] = useState(false);
    const bodyRef = useRef(initBody);
    // Key to force EmailBlockEditor remount when switching modes
    const [blockEditorKey, setBlockEditorKey] = useState(0);

    useEffect(() => {
        if (open) {
            setSubject(initSubject);
            setBody(initBody);
            setEmailBlocks(initBlocks);
            setEmailSettings(initSettings);
            setMode('blocks');
            bodyRef.current = initBody;
            setBlockEditorKey(prev => prev + 1);
            loadSignatures();
        }
    }, [open, initSubject, initBody]);

    const loadSignatures = async () => {
        try {
            const res = await axios.get(`${API}/signatures`, { headers: getAuthHeader() });
            const data = res.data || {};
            const sigs = [...(data.signatures || []), ...(data.admin_templates || [])];
            const resolvedSigs = sigs.map(s => ({
                ...s,
                content: resolveSigVars(s.content),
            }));
            setSignatures(resolvedSigs);
        } catch (err) {
            console.error('Failed to load signatures:', err);
            toast.error('Could not load email signatures');
        }
    };

    const resolveSigVars = (html) => {
        if (!html) return html;
        // v2.16.38: {{SenderPhoto}} + {{CompanyLogo}} route through the
        // permanent proxy endpoint (was 1h presigned URL → broken in
        // recipient inbox after an hour).
        const replacements = {
            '{{SenderName}}': user?.name || '',
            '{{SenderTitle}}': user?.title || '',
            '{{SenderCompany}}': user?.org_name || user?.company_name || '',
            '{{SenderEmail}}': user?.email || '',
            '{{SenderPhone}}': user?.phone || '',
            '{{SenderLinkedIn}}': user?.linkedin_url || '',
            '{{SenderAddress}}': user?.address || '',
            ...signatureAssetMergeMap(user),
        };
        Object.entries(replacements).forEach(([key, val]) => {
            html = html.split(key).join(val || '');
        });
        return html;
    };

    const handleBlocksChange = ({ blocks, settings, html }) => {
        setEmailBlocks(blocks);
        setEmailSettings(settings);
        setBody(html);
        bodyRef.current = html;
    };

    // Switch from HTML → Visual: migrate current HTML body into blocks
    const handleSwitchToBlocks = () => {
        const currentHtml = bodyRef.current || body;
        if (currentHtml && currentHtml.trim()) {
            // If we already have blocks, update the first text block with current HTML
            // Otherwise create a new text block from the HTML
            if (!emailBlocks?.length) {
                setEmailBlocks([
                    createBlock('text', { content: currentHtml }),
                    createBlock('signature'),
                ]);
            }
            // else: blocks are already synced from the visual editor, just switch back
        }
        setBlockEditorKey(prev => prev + 1);
        setMode('blocks');
    };

    // Switch from Visual → HTML: serialize blocks into body
    const handleSwitchToCode = () => {
        if (emailBlocks?.length) {
            const html = serializeEmailHtml(emailSettings || DEFAULT_EMAIL_SETTINGS, emailBlocks);
            setBody(html);
            bodyRef.current = html;
        }
        setMode('code');
    };

    const handleSave = () => {
        setSaving(true);
        const result = { subject, body: bodyRef.current || body };
        if (emailBlocks?.length) {
            result.email_blocks = emailBlocks;
            result.email_settings = emailSettings;
            // Re-serialize to ensure body is current
            result.body = serializeEmailHtml(emailSettings || DEFAULT_EMAIL_SETTINGS, emailBlocks);
        }
        onSave(result);
        setSaving(false);
        toast.success('Email saved');
        onClose();
    };

    if (!open) return null;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="flex flex-col p-0" style={{ width: '95vw', maxWidth: '95vw', height: '92vh', maxHeight: '92vh' }}>
                <DialogHeader className="px-4 pt-3 pb-2 border-b border-stone-200 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <DialogTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Email Builder
                            </DialogTitle>
                            {/* Mode toggle */}
                            <div className="flex items-center gap-0.5 bg-stone-100 rounded-lg p-0.5">
                                <button onClick={handleSwitchToBlocks}
                                    className={`flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium rounded-md transition-colors ${mode === 'blocks' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}>
                                    <Blocks className="w-3 h-3" /> Visual
                                </button>
                                <button onClick={handleSwitchToCode}
                                    className={`flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium rounded-md transition-colors ${mode === 'code' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}>
                                    <Code className="w-3 h-3" /> HTML
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button size="sm" onClick={handleSave} disabled={saving}>
                                <Save className="w-3.5 h-3.5 mr-1" /> Save Email
                            </Button>
                        </div>
                    </div>
                    {/* Subject line */}
                    <div className="mt-2">
                        <input value={subject} onChange={e => setSubject(e.target.value)}
                            placeholder="Email subject line..."
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>
                </DialogHeader>

                {/* Body */}
                <div className="flex-1 overflow-hidden">
                    {mode === 'blocks' ? (
                        <EmailBlockEditor
                            key={blockEditorKey}
                            emailBlocks={emailBlocks}
                            emailSettings={emailSettings}
                            existingBody={initBody}
                            onChange={handleBlocksChange}
                            signatures={signatures}
                            contactId={contactId}
                            dealId={dealId}
                            accountId={accountId}
                        />
                    ) : (
                        <div className="h-full px-5 py-2 overflow-hidden">
                            <RichTextEditor
                                content={body}
                                onChange={(html) => { setBody(html); bodyRef.current = html; }}
                                placeholder="Write your email HTML..."
                                showMergeVariables={true}
                                showAIWriter={true}
                                showContentLibrary={true}
                                contactId={contactId}
                            />
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
